import { Injectable } from '@angular/core';
import {
  UserSettings,
  DateFormatSettings,
  LanguageSettings,
  MeasurementSystemSettings,
  ThemeSettings,
} from '../../models/settings.model';

import { GLOBAL_CONSTANT } from '../../constants/global.constant';

/**
 * Mapper: Normalizes settings with default values
 * Pure functions, no state
 */
@Injectable({ providedIn: 'root' })
export class SettingsMapper {
  /**
   * Normalize settings with default values
   */
  toDomain(settings: UserSettings): UserSettings {
    return {
      username: settings.username ?? GLOBAL_CONSTANT.EMPTY_STRING,
      dateFormatID: settings.dateFormatID ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      languageID: settings.languageID ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      measurementSystemID: settings.measurementSystemID ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      themeID: settings.themeID ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      notificationsPush: settings.notificationsPush ?? false,
      notificationsSMS: settings.notificationsSMS ?? false,
      notificationsEmail: settings.notificationsEmail ?? false,
      notificationsWarning: settings.notificationsWarning ?? false,
      notificationsFlangeAlarmEmail: settings.notificationsFlangeAlarmEmail ?? false,
      notificationsFlangeAlarmPush: settings.notificationsFlangeAlarmPush ?? false,
      notificationsFlangeWarningEmail: settings.notificationsFlangeWarningEmail ?? false,
      notificationsFlangeWarningPush: settings.notificationsFlangeWarningPush ?? false,
      notificationsFlangeInactiveEmail: settings.notificationsFlangeInactiveEmail ?? false,
      notificationsFlangeInactivePush: settings.notificationsFlangeInactivePush ?? false,
      notificationsPipeAlarmEmail: settings.notificationsPipeAlarmEmail ?? false,
      notificationsPipeAlarmPush: settings.notificationsPipeAlarmPush ?? false,
      notificationsPipeWarningEmail: settings.notificationsPipeWarningEmail ?? false,
      notificationsPipeWarningPush: settings.notificationsPipeWarningPush ?? false,
      notificationsPipeInactiveEmail: settings.notificationsPipeInactiveEmail ?? false,
      notificationsPipeInactivePush: settings.notificationsPipeInactivePush ?? false,
      notificationsSteamtrapAlarmEmail: settings.notificationsSteamtrapAlarmEmail ?? false,
      notificationsSteamtrapAlarmPush: settings.notificationsSteamtrapAlarmPush ?? false,
      notificationsSteamtrapWarningEmail: settings.notificationsSteamtrapWarningEmail ?? false,
      notificationsSteamtrapWarningPush: settings.notificationsSteamtrapWarningPush ?? false,
      notificationsSteamtrapInactiveEmail: settings.notificationsSteamtrapInactiveEmail ?? false,
      notificationsSteamtrapInactivePush: settings.notificationsSteamtrapInactivePush ?? false,
      notificationsNanoCriticalEmail: settings.notificationsNanoCriticalEmail ?? false,
      notificationsNanoCriticalPush: settings.notificationsNanoCriticalPush ?? false,
      notificationsNanoWarningEmail: settings.notificationsNanoWarningEmail ?? false,
      notificationsNanoWarningPush: settings.notificationsNanoWarningPush ?? false,
      notificationsNanoWorkingEmail: settings.notificationsNanoWorkingEmail ?? false,
      notificationsNanoWorkingPush: settings.notificationsNanoWorkingPush ?? false,
      notificationsNanoInactiveEmail: settings.notificationsNanoInactiveEmail ?? false,
      notificationsNanoInactivePush: settings.notificationsNanoInactivePush ?? false,
      name: settings.name ?? GLOBAL_CONSTANT.EMPTY_STRING,
      surname: settings.surname ?? GLOBAL_CONSTANT.EMPTY_STRING,
      designation: settings.designation ?? GLOBAL_CONSTANT.EMPTY_STRING,
      phonenumber: settings.phonenumber ?? GLOBAL_CONSTANT.EMPTY_STRING,
      email: settings.email ?? GLOBAL_CONSTANT.EMPTY_STRING,
      countryID: settings.countryID ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      dateFormats: (settings.dateFormats ?? []).map((df) => this.normalizeDateFormat(df)),
      languages: (settings.languages ?? []).map((lang) => this.normalizeLanguage(lang)),
      measurementSystems: (settings.measurementSystems ?? []).map((ms) =>
        this.normalizeMeasurementSystem(ms)
      ),
      themes: (settings.themes ?? []).map((theme) => this.normalizeTheme(theme)),
    };
  }

  /**
   * Normalize DateFormatSettings with defaults
   */
  private normalizeDateFormat(format: DateFormatSettings): DateFormatSettings {
    return {
      id: format.id ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      name: format.name ?? GLOBAL_CONSTANT.EMPTY_STRING,
      formatString: format.formatString ?? GLOBAL_CONSTANT.EMPTY_STRING,
    };
  }

  /**
   * Normalize LanguageSettings with defaults
   */
  private normalizeLanguage(lang: LanguageSettings): LanguageSettings {
    return {
      id: lang.id ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      name: lang.name ?? GLOBAL_CONSTANT.EMPTY_STRING,
      abbreviation: lang.abbreviation ?? GLOBAL_CONSTANT.EMPTY_STRING,
    };
  }

  /**
   * Normalize MeasurementSystemSettings with defaults
   */
  private normalizeMeasurementSystem(system: MeasurementSystemSettings): MeasurementSystemSettings {
    return {
      id: system.id ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      name: system.name ?? GLOBAL_CONSTANT.EMPTY_STRING,
      abbreviation: system.abbreviation,
    };
  }

  /**
   * Normalize ThemeSettings with defaults
   */
  private normalizeTheme(theme: ThemeSettings): ThemeSettings {
    return {
      id: theme.id ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER,
      name: theme.name ?? GLOBAL_CONSTANT.EMPTY_STRING,
      image: theme.image,
    };
  }

  /**
   * Convert to API format (no-op since already in correct format)
   */
  toDTO(settings: UserSettings): UserSettings {
    return settings;
  }
}
